import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cartApi } from '@/api/endpoints';
import { useAuthStore } from '@/store/authStore';

/** 장바구니 페이지와 플로팅 장바구니가 함께 쓰는 조회·선택·변경 로직. */
export function useCart() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [selected, setSelected] = useState<number[]>([]);

  const query = useQuery({
    queryKey: ['cart'],
    queryFn: cartApi.get,
    enabled: Boolean(user),
  });
  const { data } = query;

  // 주문 가능한 항목은 기본으로 체크해 둔다.
  useEffect(() => {
    if (data) {
      setSelected(data.items.filter((item) => item.orderable).map((item) => item.cartItemId));
    }
  }, [data]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['cart'] });

  const changeQuantity = useMutation({
    mutationFn: ({ cartItemId, quantity }: { cartItemId: number; quantity: number }) =>
      cartApi.changeQuantity(cartItemId, quantity),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (cartItemId: number) => cartApi.remove(cartItemId),
    onSuccess: invalidate,
  });

  const items = data?.items ?? [];
  const selectedItems = items.filter((item) => selected.includes(item.cartItemId));
  const itemTotal = selectedItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const shippingFee = itemTotal === 0 || itemTotal >= 50000 ? 0 : 3000;

  const toggle = (cartItemId: number) =>
    setSelected((prev) =>
      prev.includes(cartItemId) ? prev.filter((id) => id !== cartItemId) : [...prev, cartItemId],
    );

  return {
    user,
    ...query,
    selected,
    selectedItems,
    itemTotal,
    shippingFee,
    toggle,
    changeQuantity,
    remove,
  };
}

export type CartController = ReturnType<typeof useCart>;
